import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiMapPin,
  FiMenu,
  FiPlus,
  FiPrinter,
  FiSave,
  FiShare2,
  FiTag,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
  useParams,
} from "react-router-dom";

import {
  createQuotation,
  fetchQuotationById,
  fetchQuotationCreateData,
  quotationCreateDataSelector,
  quotationLoadingSelector,
  selectedQuotationSelector,
  updateQuotation,
} from "../../../../../../store/billing";

import "./QuotationFormPage.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const TAX_OPTIONS = [
  0,
  0.25,
  3,
  5,
  12,
  18,
  28,
];

const UNIT_OPTIONS = [
  {
    value: "none",
    label: "NONE",
  },
  {
    value: "piece",
    label: "PCS",
  },
  {
    value: "nos",
    label: "NOS",
  },
  {
    value: "unit",
    label: "UNIT",
  },
  {
    value: "set",
    label: "SET",
  },
  {
    value: "pair",
    label: "PAIR",
  },
  {
    value: "box",
    label: "BOX",
  },
  {
    value: "kg",
    label: "KG",
  },
  {
    value: "gram",
    label: "GRAM",
  },
  {
    value: "meter",
    label: "METER",
  },
  {
    value: "feet",
    label: "FEET",
  },
  {
    value: "litre",
    label: "LITRE",
  },
  {
    value: "hour",
    label: "HOUR",
  },
  {
    value: "service",
    label: "SERVICE",
  },
];

const INDIA_STATES = [
  {
    name: "Andaman and Nicobar Islands",
    code: "35",
  },
  {
    name: "Andhra Pradesh",
    code: "37",
  },
  {
    name: "Arunachal Pradesh",
    code: "12",
  },
  {
    name: "Assam",
    code: "18",
  },
  {
    name: "Bihar",
    code: "10",
  },
  {
    name: "Chandigarh",
    code: "04",
  },
  {
    name: "Chhattisgarh",
    code: "22",
  },
  {
    name: "Dadra and Nagar Haveli and Daman and Diu",
    code: "26",
  },
  {
    name: "Delhi",
    code: "07",
  },
  {
    name: "Goa",
    code: "30",
  },
  {
    name: "Gujarat",
    code: "24",
  },
  {
    name: "Haryana",
    code: "06",
  },
  {
    name: "Himachal Pradesh",
    code: "02",
  },
  {
    name: "Jammu and Kashmir",
    code: "01",
  },
  {
    name: "Jharkhand",
    code: "20",
  },
  {
    name: "Karnataka",
    code: "29",
  },
  {
    name: "Kerala",
    code: "32",
  },
  {
    name: "Ladakh",
    code: "38",
  },
  {
    name: "Lakshadweep",
    code: "31",
  },
  {
    name: "Madhya Pradesh",
    code: "23",
  },
  {
    name: "Maharashtra",
    code: "27",
  },
  {
    name: "Manipur",
    code: "14",
  },
  {
    name: "Meghalaya",
    code: "17",
  },
  {
    name: "Mizoram",
    code: "15",
  },
  {
    name: "Nagaland",
    code: "13",
  },
  {
    name: "Odisha",
    code: "21",
  },
  {
    name: "Puducherry",
    code: "34",
  },
  {
    name: "Punjab",
    code: "03",
  },
  {
    name: "Rajasthan",
    code: "08",
  },
  {
    name: "Sikkim",
    code: "11",
  },
  {
    name: "Tamil Nadu",
    code: "33",
  },
  {
    name: "Telangana",
    code: "36",
  },
  {
    name: "Tripura",
    code: "16",
  },
  {
    name: "Uttar Pradesh",
    code: "09",
  },
  {
    name: "Uttarakhand",
    code: "05",
  },
  {
    name: "West Bengal",
    code: "19",
  },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const createTemporaryId = () => {
  return `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
};

const roundMoney = (value) => {
  return (
    Math.round(
      (toNumber(value) +
        Number.EPSILON) *
        100
    ) / 100
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }
  ).format(
    toNumber(value)
  );
};

const formatDateInput = (value) => {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date
    .toISOString()
    .slice(0, 10);
};

const addDays = (
  dateValue,
  days
) => {
  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  date.setDate(
    date.getDate() +
      Number(days || 0)
  );

  return date
    .toISOString()
    .slice(0, 10);
};

const getCustomerId = (
  customer
) => {
  return (
    customer?._id ||
    customer?.id ||
    ""
  );
};

const getCustomerName = (
  customer
) => {
  return (
    customer?.customerName ||
    customer?.name ||
    customer?.fullName ||
    customer?.companyName ||
    ""
  );
};

const getCustomerMobile = (
  customer
) => {
  return (
    customer?.mobile ||
    customer?.phone ||
    customer?.mobileNumber ||
    ""
  );
};

const getCustomerDisplayName = (
  customer
) => {
  return [
    getCustomerName(
      customer
    ),

    getCustomerMobile(
      customer
    ),
  ]
    .filter(Boolean)
    .join(" - ");
};

const getProductId = (
  product
) => {
  return (
    product?._id ||
    product?.id ||
    ""
  );
};

const getProductName = (
  product
) => {
  return (
    product?.productName ||
    product?.name ||
    product?.title ||
    ""
  );
};

const getProductSku = (
  product
) => {
  return (
    product?.sku ||
    product?.productCode ||
    product?.code ||
    ""
  );
};

const getProductDisplayName = (
  product
) => {
  return [
    getProductName(
      product
    ),

    getProductSku(
      product
    )
      ? `SKU: ${getProductSku(
          product
        )}`
      : "",
  ]
    .filter(Boolean)
    .join(" - ");
};

const createEmptyItem = (
  defaults = {}
) => ({
  temporaryId:
    createTemporaryId(),

  productId: "",
  productSearch: "",

  itemType:
    "MANUAL",

  itemName: "",
  description: "",
  hsnSacCode: "",

  quantity: 1,

  unit:
    defaults.defaultUnit ||
    "none",

  rate: 0,

  priceMode:
    defaults.defaultPriceMode ||
    "TAX_EXCLUSIVE",

  discountType:
    "NONE",

  discountValue: 0,

  taxPercentage:
    toNumber(
      defaults.defaultTaxPercentage
    ),

  cessPercentage: 0,
});

const calculateItem = (
  item,
  isInterState
) => {
  const quantity =
    Math.max(
      toNumber(
        item.quantity
      ),
      0
    );

  const rate =
    Math.max(
      toNumber(
        item.rate
      ),
      0
    );

  const taxPercentage =
    Math.max(
      toNumber(
        item.taxPercentage
      ),
      0
    );

  const cessPercentage =
    Math.max(
      toNumber(
        item.cessPercentage
      ),
      0
    );

  const grossAmount =
    roundMoney(
      quantity *
        rate
    );

  let discountAmount = 0;

  if (
    item.discountType ===
    "PERCENTAGE"
  ) {
    const percentage =
      Math.min(
        Math.max(
          toNumber(
            item.discountValue
          ),
          0
        ),
        100
      );

    discountAmount =
      roundMoney(
        grossAmount *
          percentage /
          100
      );
  }

  if (
    item.discountType ===
    "FLAT"
  ) {
    discountAmount =
      roundMoney(
        Math.min(
          Math.max(
            toNumber(
              item.discountValue
            ),
            0
          ),
          grossAmount
        )
      );
  }

  const amountAfterDiscount =
    roundMoney(
      Math.max(
        grossAmount -
          discountAmount,
        0
      )
    );

  let taxableAmount = 0;
  let taxAmount = 0;
  let cessAmount = 0;
  let totalAmount = 0;

  if (
    item.priceMode ===
    "TAX_INCLUSIVE"
  ) {
    const combinedPercentage =
      taxPercentage +
      cessPercentage;

    taxableAmount =
      combinedPercentage > 0
        ? roundMoney(
            amountAfterDiscount /
              (
                1 +
                combinedPercentage /
                  100
              )
          )
        : amountAfterDiscount;

    const totalTaxAndCess =
      roundMoney(
        amountAfterDiscount -
          taxableAmount
      );

    if (
      combinedPercentage > 0
    ) {
      taxAmount =
        roundMoney(
          totalTaxAndCess *
            taxPercentage /
            combinedPercentage
        );

      cessAmount =
        roundMoney(
          totalTaxAndCess -
            taxAmount
        );
    }

    totalAmount =
      amountAfterDiscount;
  } else {
    taxableAmount =
      amountAfterDiscount;

    taxAmount =
      roundMoney(
        taxableAmount *
          taxPercentage /
          100
      );

    cessAmount =
      roundMoney(
        taxableAmount *
          cessPercentage /
          100
      );

    totalAmount =
      roundMoney(
        taxableAmount +
          taxAmount +
          cessAmount
      );
  }

  const cgstAmount =
    isInterState
      ? 0
      : roundMoney(
          taxAmount / 2
        );

  const sgstAmount =
    isInterState
      ? 0
      : roundMoney(
          taxAmount -
            cgstAmount
        );

  const igstAmount =
    isInterState
      ? taxAmount
      : 0;

  return {
    ...item,

    quantity,
    rate,

    taxPercentage,
    cessPercentage,

    grossAmount,
    discountAmount,
    taxableAmount,

    cgstAmount,
    sgstAmount,
    igstAmount,

    taxAmount,
    cessAmount,
    totalAmount,
  };
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const QuotationFormPage = () => {
  const dispatch =
    useDispatch();

  const history =
    useHistory();

  const { id } =
    useParams();

  const shareMenuRef =
    useRef(null);

  const draggedRowIdRef =
    useRef(null);

  const isEditMode =
    Boolean(id);

  const createData =
    useSelector(
      quotationCreateDataSelector
    ) || {};

  const selectedQuotation =
    useSelector(
      selectedQuotationSelector
    );

  const loadingState =
    useSelector(
      quotationLoadingSelector
    ) || {};

  const settings =
    createData.settings ||
    createData.billingSettings ||
    {};

  const customers =
    createData.customers ||
    createData.customerOptions ||
    [];

  const products =
    createData.products ||
    createData.productOptions ||
    [];

  const branches =
    createData.branches ||
    [];

  const firms =
    createData.firms ||
    [];

  const warehouses =
    createData.warehouses ||
    [];

  const salespersons =
    createData.salespersons ||
    createData.employees ||
    [];

  const today =
    formatDateInput(
      new Date()
    );

  const [
    formData,
    setFormData,
  ] = useState({
    customerId: "",
    customerSearch: "",

    customerName: "",
    customerMobile: "",
    customerEmail: "",
    customerGstin: "",

    customerAddress: "",
    customerCity: "",
    customerState: "",
    customerStateCode: "",
    customerPincode: "",

    documentDate:
      today,

    validUntil:
      addDays(
        today,
        15
      ),

    validityDays: 15,

    salesChannel:
      "STORE",

    priceMode:
      settings.defaultPriceMode ||
      "TAX_EXCLUSIVE",

    placeOfSupply:
      settings.state ||
      "Rajasthan",

    placeOfSupplyStateCode:
      settings.stateCode ||
      "08",

    branchId: "",
    firmId: "",
    warehouseId: "",
    salesperson: "",

    termsAndConditions:
      settings.defaultTerms ||
      "",

    customerNotes:
      settings.defaultCustomerNotes ||
      "",

    internalNotes: "",

    enableRoundOff:
      settings.enableRoundOff !==
      false,
  });

  const [
    items,
    setItems,
  ] = useState([
    createEmptyItem({
      defaultUnit:
        settings.defaultUnit,

      defaultPriceMode:
        settings.defaultPriceMode,

      defaultTaxPercentage:
        settings.defaultTaxPercentage,
    }),
  ]);

  const [
    showAddressDetails,
    setShowAddressDetails,
  ] = useState(false);

  const [
    showGstDetails,
    setShowGstDetails,
  ] = useState(false);

  const [
    draggedOverRowId,
    setDraggedOverRowId,
  ] = useState("");

  const [
    showShareMenu,
    setShowShareMenu,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const saving =
    Boolean(
      loadingState.createLoading ||
      loadingState.updateLoading
    );

  const businessStateCode =
    String(
      settings.stateCode ||
      createData.businessStateCode ||
      "08"
    ).padStart(
      2,
      "0"
    );

  const selectedStateCode =
    String(
      formData.placeOfSupplyStateCode ||
      ""
    ).padStart(
      2,
      "0"
    );

  const isInterState =
    Boolean(
      selectedStateCode &&
      businessStateCode &&
      selectedStateCode !==
        businessStateCode
    );

  /*
  |--------------------------------------------------------------------------
  | LOAD DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    dispatch(
      fetchQuotationCreateData()
    );

    if (id) {
      dispatch(
        fetchQuotationById(
          id
        )
      );
    }
  }, [
    dispatch,
    id,
  ]);

  /*
  |--------------------------------------------------------------------------
  | CLOSE SHARE MENU
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleOutsideClick = (
      event
    ) => {
      if (
        shareMenuRef.current &&
        !shareMenuRef.current.contains(
          event.target
        )
      ) {
        setShowShareMenu(
          false
        );
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | EDIT MODE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (
      !isEditMode ||
      !selectedQuotation
    ) {
      return;
    }

    const customer =
      selectedQuotation.customer ||
      {};

    const customerSnapshot =
      selectedQuotation.customerSnapshot ||
      {};

    const billingAddress =
      selectedQuotation.billingAddressSnapshot ||
      selectedQuotation.billingAddress ||
      {};

    const addressValue =
      billingAddress.addressLine1 ||
      billingAddress.address ||
      customerSnapshot.address ||
      "";

    const cityValue =
      billingAddress.city ||
      customerSnapshot.city ||
      "";

    const stateValue =
      billingAddress.state ||
      customerSnapshot.state ||
      "";

    const stateCodeValue =
      billingAddress.stateCode ||
      customerSnapshot.stateCode ||
      "";

    const pincodeValue =
      billingAddress.pincode ||
      customerSnapshot.pincode ||
      "";

    const gstinValue =
      customerSnapshot.gstin ||
      customer.gstin ||
      "";

    setShowAddressDetails(
      Boolean(
        addressValue ||
        cityValue ||
        pincodeValue
      )
    );

    setShowGstDetails(
      Boolean(
        gstinValue
      )
    );

    setFormData({
      customerId:
        getCustomerId(
          customer
        ) ||
        selectedQuotation.customerId ||
        "",

      customerSearch:
        getCustomerDisplayName(
          customer
        ) ||
        customerSnapshot.customerName ||
        "",

      customerName:
        customerSnapshot.customerName ||
        getCustomerName(
          customer
        ) ||
        "",

      customerMobile:
        customerSnapshot.mobile ||
        getCustomerMobile(
          customer
        ) ||
        "",

      customerEmail:
        customerSnapshot.email ||
        customer.email ||
        "",

      customerGstin:
        gstinValue,

      customerAddress:
        addressValue,

      customerCity:
        cityValue,

      customerState:
        stateValue,

      customerStateCode:
        stateCodeValue,

      customerPincode:
        pincodeValue,

      documentDate:
        formatDateInput(
          selectedQuotation.documentDate
        ),

      validUntil:
        formatDateInput(
          selectedQuotation.validUntil
        ),

      validityDays:
        selectedQuotation.validityDays ||
        15,

      salesChannel:
        selectedQuotation.salesChannel ||
        "STORE",

      priceMode:
        selectedQuotation.priceMode ||
        "TAX_EXCLUSIVE",

      placeOfSupply:
        selectedQuotation.placeOfSupply ||
        stateValue ||
        "",

      placeOfSupplyStateCode:
        selectedQuotation.placeOfSupplyStateCode ||
        stateCodeValue ||
        "",

      branchId:
        selectedQuotation.branchId?._id ||
        selectedQuotation.branchId ||
        "",

      firmId:
        selectedQuotation.firmId?._id ||
        selectedQuotation.firmId ||
        "",

      warehouseId:
        selectedQuotation.warehouseId?._id ||
        selectedQuotation.warehouseId ||
        "",

      salesperson:
        selectedQuotation.salesperson?._id ||
        selectedQuotation.salesperson ||
        "",

      termsAndConditions:
        selectedQuotation.termsAndConditions ||
        "",

      customerNotes:
        selectedQuotation.customerNotes ||
        "",

      internalNotes:
        selectedQuotation.internalNotes ||
        "",

      enableRoundOff:
        true,
    });

    const existingItems =
      Array.isArray(
        selectedQuotation.items
      ) &&
      selectedQuotation.items.length >
        0
        ? selectedQuotation.items
        : [
            createEmptyItem(),
          ];

    setItems(
      existingItems.map(
        (item) => ({
          temporaryId:
            item._id ||
            createTemporaryId(),

          productId:
            item.productId?._id ||
            item.productId ||
            "",

          productSearch:
            item.itemName ||
            item.productName ||
            "",

          itemType:
            item.itemType ||
            (
              item.productId
                ? "INVENTORY"
                : "MANUAL"
            ),

          itemName:
            item.itemName ||
            item.productName ||
            "",

          description:
            item.description ||
            "",

          hsnSacCode:
            item.hsnSacCode ||
            item.hsnCode ||
            "",

          quantity:
            item.quantity ||
            1,

          unit:
            item.unit ||
            "none",

          rate:
            item.rate ??
            item.unitPrice ??
            0,

          priceMode:
            item.priceMode ||
            selectedQuotation.priceMode ||
            "TAX_EXCLUSIVE",

          discountType:
            item.discountType ||
            "NONE",

          discountValue:
            item.discountValue ||
            0,

          taxPercentage:
            item.taxPercentage ??
            item.taxRate ??
            0,

          cessPercentage:
            item.cessPercentage ||
            0,
        })
      )
    );
  }, [
    isEditMode,
    selectedQuotation,
  ]);

  /*
  |--------------------------------------------------------------------------
  | CALCULATIONS
  |--------------------------------------------------------------------------
  */

  const calculatedItems =
    useMemo(() => {
      return items.map(
        (item) =>
          calculateItem(
            item,
            isInterState
          )
      );
    }, [
      items,
      isInterState,
    ]);

  const totals = useMemo(() => {
    const result = calculatedItems.reduce(
      (total, item) => {
        const hasItem = Boolean(
          item.itemName?.trim() ||
            item.productSearch?.trim() ||
            item.productId
        );

        if (!hasItem) {
          return total;
        }

        return {
          itemCount: total.itemCount + 1,
          totalQuantity:
            total.totalQuantity + toNumber(item.quantity),
          subtotal:
            total.subtotal + toNumber(item.grossAmount),
          discountAmount:
            total.discountAmount + toNumber(item.discountAmount),
          taxableAmount:
            total.taxableAmount + toNumber(item.taxableAmount),
          cgstAmount:
            total.cgstAmount + toNumber(item.cgstAmount),
          sgstAmount:
            total.sgstAmount + toNumber(item.sgstAmount),
          igstAmount:
            total.igstAmount + toNumber(item.igstAmount),
          taxAmount:
            total.taxAmount + toNumber(item.taxAmount),
          cessAmount:
            total.cessAmount + toNumber(item.cessAmount),
          totalBeforeRoundOff:
            total.totalBeforeRoundOff + toNumber(item.totalAmount),
        };
      },
      {
        itemCount: 0,
        totalQuantity: 0,
        subtotal: 0,
        discountAmount: 0,
        taxableAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        taxAmount: 0,
        cessAmount: 0,
        totalBeforeRoundOff: 0,
      }
    );

    const subtotal = roundMoney(result.subtotal);
    const discountAmount = roundMoney(result.discountAmount);
    const taxableAmount = roundMoney(result.taxableAmount);
    const cgstAmount = roundMoney(result.cgstAmount);
    const sgstAmount = roundMoney(result.sgstAmount);
    const igstAmount = roundMoney(result.igstAmount);
    const taxAmount = roundMoney(result.taxAmount);
    const cessAmount = roundMoney(result.cessAmount);
    const totalBeforeRoundOff = roundMoney(
      result.totalBeforeRoundOff
    );

    const roundedTotal = Math.round(totalBeforeRoundOff);
    const roundOffAmount = formData.enableRoundOff
      ? roundMoney(roundedTotal - totalBeforeRoundOff)
      : 0;

    return {
      ...result,
      subtotal,
      discountAmount,
      taxableAmount,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      cessAmount,
      totalBeforeRoundOff,
      roundOffAmount,
      grandTotal: roundMoney(
        totalBeforeRoundOff + roundOffAmount
      ),
    };
  }, [calculatedItems, formData.enableRoundOff]);

  /*
  |--------------------------------------------------------------------------
  | CUSTOMER HANDLERS
  |--------------------------------------------------------------------------
  */

  const handleCustomerSearchChange = (
    event
  ) => {
    const value =
      event.target.value;

    const normalizedValue =
      value
        .trim()
        .toLowerCase();

    const matchedCustomer =
      customers.find(
        (customer) =>
          getCustomerDisplayName(
            customer
          )
            .trim()
            .toLowerCase() ===
          normalizedValue
      );

    if (
      matchedCustomer
    ) {
      const customerAddress =
        matchedCustomer.address ||
        matchedCustomer.billingAddress ||
        {};

      const addressValue =
        matchedCustomer.addressLine1 ||
        customerAddress.addressLine1 ||
        (
          typeof customerAddress ===
          "string"
            ? customerAddress
            : ""
        );

      const cityValue =
        matchedCustomer.city ||
        customerAddress.city ||
        "";

      const stateValue =
        matchedCustomer.state ||
        customerAddress.state ||
        "";

      const stateCodeValue =
        matchedCustomer.stateCode ||
        customerAddress.stateCode ||
        "";

      const pincodeValue =
        matchedCustomer.pincode ||
        customerAddress.pincode ||
        "";

      const gstinValue =
        matchedCustomer.gstin ||
        matchedCustomer.gstNumber ||
        "";

      setShowAddressDetails(
        Boolean(
          addressValue ||
          cityValue ||
          pincodeValue
        )
      );

      setShowGstDetails(
        Boolean(
          gstinValue
        )
      );

      setFormData(
        (current) => ({
          ...current,

          customerId:
            getCustomerId(
              matchedCustomer
            ),

          customerSearch:
            value,

          customerName:
            getCustomerName(
              matchedCustomer
            ),

          customerMobile:
            getCustomerMobile(
              matchedCustomer
            ),

          customerEmail:
            matchedCustomer.email ||
            "",

          customerGstin:
            gstinValue,

          customerAddress:
            addressValue,

          customerCity:
            cityValue,

          customerState:
            stateValue,

          customerStateCode:
            stateCodeValue,

          customerPincode:
            pincodeValue,

          placeOfSupply:
            stateValue ||
            current.placeOfSupply,

          placeOfSupplyStateCode:
            stateCodeValue ||
            current.placeOfSupplyStateCode,
        })
      );

      return;
    }

    setFormData(
      (current) => ({
        ...current,

        customerId:
          "",

        customerSearch:
          value,

        customerName:
          value,
      })
    );
  };

  const handleCustomerFieldChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setFormData(
      (current) => ({
        ...current,

        [name]:
          value,
      })
    );
  };

  const handleStateChange = (
    event
  ) => {
    const stateCode =
      event.target.value;

    const selectedState =
      INDIA_STATES.find(
        (state) =>
          state.code ===
          stateCode
      );

    setFormData(
      (current) => ({
        ...current,

        placeOfSupply:
          selectedState?.name ||
          "",

        placeOfSupplyStateCode:
          stateCode,

        customerState:
          selectedState?.name ||
          "",

        customerStateCode:
          stateCode,
      })
    );
  };

  const clearAddressDetails = () => {
    setFormData(
      (current) => ({
        ...current,

        customerAddress:
          "",

        customerCity:
          "",

        customerPincode:
          "",
      })
    );

    setShowAddressDetails(
      false
    );
  };

  const clearGstDetails = () => {
    setFormData(
      (current) => ({
        ...current,

        customerGstin:
          "",
      })
    );

    setShowGstDetails(
      false
    );
  };

  /*
  |--------------------------------------------------------------------------
  | GENERAL FORM HANDLER
  |--------------------------------------------------------------------------
  */

  const handleFieldChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData(
      (current) => {
        const updated = {
          ...current,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        };

        if (
          name ===
          "documentDate"
        ) {
          updated.validUntil =
            addDays(
              value,
              current.validityDays ||
                15
            );
        }

        if (
          name ===
          "validityDays"
        ) {
          updated.validUntil =
            addDays(
              current.documentDate,
              value
            );
        }

        return updated;
      }
    );
  };

  /*
  |--------------------------------------------------------------------------
  | ITEM HANDLERS
  |--------------------------------------------------------------------------
  */

  const updateItem = (
    temporaryId,
    key,
    value
  ) => {
    setItems(
      (currentItems) =>
        currentItems.map(
          (item) =>
            item.temporaryId ===
            temporaryId
              ? {
                  ...item,

                  [key]:
                    value,
                }
              : item
        )
    );
  };

  const handleProductSearchChange = (
    temporaryId,
    value
  ) => {
    const normalizedValue =
      value
        .trim()
        .toLowerCase();

    const matchedProduct =
      products.find(
        (product) =>
          getProductDisplayName(
            product
          )
            .trim()
            .toLowerCase() ===
          normalizedValue
      );

    setItems(
      (currentItems) =>
        currentItems.map(
          (item) => {
            if (
              item.temporaryId !==
              temporaryId
            ) {
              return item;
            }

            if (
              matchedProduct
            ) {
              return {
                ...item,

                productId:
                  getProductId(
                    matchedProduct
                  ),

                productSearch:
                  value,

                itemType:
                  "INVENTORY",

                itemName:
                  getProductName(
                    matchedProduct
                  ),

                description:
                  matchedProduct.shortDescription ||
                  matchedProduct.description ||
                  "",

                hsnSacCode:
                  matchedProduct.hsnSacCode ||
                  matchedProduct.hsnCode ||
                  "",

                unit:
                  matchedProduct.unit ||
                  settings.defaultUnit ||
                  "none",

                rate:
                  matchedProduct.sellingPrice ??
                  matchedProduct.salePrice ??
                  matchedProduct.discountedPrice ??
                  matchedProduct.price ??
                  0,

                priceMode:
                  matchedProduct.priceIncludesTax
                    ? "TAX_INCLUSIVE"
                    : (
                        settings.defaultPriceMode ||
                        formData.priceMode ||
                        "TAX_EXCLUSIVE"
                      ),

                taxPercentage:
                  matchedProduct.taxPercentage ??
                  matchedProduct.gstRate ??
                  settings.defaultTaxPercentage ??
                  0,

                cessPercentage:
                  matchedProduct.cessPercentage ||
                  0,
              };
            }

            return {
              ...item,

              productId:
                "",

              productSearch:
                value,

              itemType:
                "MANUAL",

              itemName:
                value,
            };
          }
        )
    );
  };

  const addItem = () => {
    setItems(
      (currentItems) => [
        ...currentItems,

        createEmptyItem({
          defaultUnit:
            settings.defaultUnit,

          defaultPriceMode:
            formData.priceMode,

          defaultTaxPercentage:
            settings.defaultTaxPercentage,
        }),
      ]
    );
  };

  const removeItem = (
    temporaryId
  ) => {
    setItems(
      (currentItems) => {
        if (
          currentItems.length ===
          1
        ) {
          return [
            createEmptyItem({
              defaultUnit:
                settings.defaultUnit,

              defaultPriceMode:
                formData.priceMode,

              defaultTaxPercentage:
                settings.defaultTaxPercentage,
            }),
          ];
        }

        return currentItems.filter(
          (item) =>
            item.temporaryId !==
            temporaryId
        );
      }
    );
  };

  /*
  |--------------------------------------------------------------------------
  | DRAG AND DROP
  |--------------------------------------------------------------------------
  */

  const handleDragStart = (
    event,
    temporaryId
  ) => {
    draggedRowIdRef.current =
      temporaryId;

    event.dataTransfer.effectAllowed =
      "move";

    event.dataTransfer.setData(
      "text/plain",
      temporaryId
    );

    event.currentTarget
      .closest("tr")
      ?.classList.add("quotation-row-dragging");
  };

  const handleDragOver = (
    event,
    temporaryId
  ) => {
    event.preventDefault();

    event.dataTransfer.dropEffect =
      "move";

    setDraggedOverRowId(
      temporaryId
    );
  };

  const handleDragLeave = (
    temporaryId
  ) => {
    if (
      draggedOverRowId ===
      temporaryId
    ) {
      setDraggedOverRowId(
        ""
      );
    }
  };

  const handleDrop = (
    event,
    targetTemporaryId
  ) => {
    event.preventDefault();

    const sourceTemporaryId =
      draggedRowIdRef.current ||
      event.dataTransfer.getData(
        "text/plain"
      );

    if (
      !sourceTemporaryId ||
      sourceTemporaryId ===
        targetTemporaryId
    ) {
      setDraggedOverRowId(
        ""
      );

      return;
    }

    setItems(
      (currentItems) => {
        const sourceIndex =
          currentItems.findIndex(
            (item) =>
              item.temporaryId ===
              sourceTemporaryId
          );

        const targetIndex =
          currentItems.findIndex(
            (item) =>
              item.temporaryId ===
              targetTemporaryId
          );

        if (
          sourceIndex < 0 ||
          targetIndex < 0
        ) {
          return currentItems;
        }

        const reorderedItems = [
          ...currentItems,
        ];

        const [
          movedItem,
        ] =
          reorderedItems.splice(
            sourceIndex,
            1
          );

        reorderedItems.splice(
          targetIndex,
          0,
          movedItem
        );

        return reorderedItems;
      }
    );

    draggedRowIdRef.current =
      null;

    setDraggedOverRowId(
      ""
    );
  };

  const handleDragEnd = (
    event
  ) => {
    event.currentTarget
      .closest("tr")
      ?.classList.remove("quotation-row-dragging");

    draggedRowIdRef.current =
      null;

    setDraggedOverRowId(
      ""
    );
  };

  /*
  |--------------------------------------------------------------------------
  | VALIDATION
  |--------------------------------------------------------------------------
  */

  const validateForm = () => {
    if (
      !formData.customerName.trim()
    ) {
      return "Please select an existing customer or enter a customer name.";
    }

    if (
      !formData.documentDate
    ) {
      return "Quotation date is required.";
    }

    if (
      !formData.validUntil
    ) {
      return "Valid until date is required.";
    }

    const validItems =
      calculatedItems.filter(
        (item) =>
          item.itemName?.trim() &&
          item.quantity > 0
      );

    if (
      validItems.length ===
      0
    ) {
      return "Please add at least one product or manual item.";
    }

    const invalidQuantityItem =
      validItems.find(
        (item) =>
          item.quantity <= 0
      );

    if (
      invalidQuantityItem
    ) {
      return "Item quantity must be greater than zero.";
    }

    return "";
  };

  /*
  |--------------------------------------------------------------------------
  | PAYLOAD
  |--------------------------------------------------------------------------
  */

  const buildPayload = (
    saveAsDraft
  ) => {
    const customerSnapshot = {
      customerName:
        formData.customerName.trim(),

      mobile:
        formData.customerMobile.trim(),

      email:
        formData.customerEmail
          .trim()
          .toLowerCase(),

      gstin:
        formData.customerGstin
          .trim()
          .toUpperCase(),

      address:
        formData.customerAddress.trim(),

      addressLine1:
        formData.customerAddress.trim(),

      city:
        formData.customerCity.trim(),

      state:
        formData.customerState ||
        formData.placeOfSupply,

      stateCode:
        formData.customerStateCode ||
        formData.placeOfSupplyStateCode,

      pincode:
        formData.customerPincode.trim(),

      country:
        "India",
    };

    const addressSnapshot = {
      address:
        formData.customerAddress.trim(),

      addressLine1:
        formData.customerAddress.trim(),

      city:
        formData.customerCity.trim(),

      state:
        formData.customerState ||
        formData.placeOfSupply,

      stateCode:
        formData.customerStateCode ||
        formData.placeOfSupplyStateCode,

      pincode:
        formData.customerPincode.trim(),

      country:
        "India",
    };

    return {
      customerId:
        formData.customerId ||
        null,

      customerSnapshot,

      billingAddress:
        addressSnapshot,

      shippingAddress:
        addressSnapshot,

      documentDate:
        formData.documentDate,

      validUntil:
        formData.validUntil,

      validityDays:
        toNumber(
          formData.validityDays
        ),

      salesChannel:
        formData.salesChannel,

      priceMode:
        formData.priceMode,

      placeOfSupply:
        formData.placeOfSupply,

      placeOfSupplyStateCode:
        formData.placeOfSupplyStateCode,

      branchId:
        formData.branchId ||
        null,

      firmId:
        formData.firmId ||
        null,

      warehouseId:
        formData.warehouseId ||
        null,

      salesperson:
        formData.salesperson ||
        null,

      termsAndConditions:
        formData.termsAndConditions,

      customerNotes:
        formData.customerNotes,

      internalNotes:
        formData.internalNotes,

      saveAsDraft,

      items: calculatedItems
        .filter(
          (item) =>
            Boolean(
              item.itemName?.trim() ||
                item.productSearch?.trim() ||
                item.productId
            ) && item.quantity > 0
        )
        .map((item, index) => {
          const resolvedItemName =
            item.itemName?.trim() ||
            item.productSearch?.trim() ||
            "Manual Item";

          return {
            sortOrder: index + 1,
            itemType: item.productId
              ? "INVENTORY"
              : "MANUAL",
            productId: item.productId || undefined,
            itemName: resolvedItemName,
            productName: resolvedItemName,
            description: item.description || "",
            hsnSacCode: item.hsnSacCode || "",
            hsnCode: item.hsnSacCode || "",
            quantity: toNumber(item.quantity),
            unit: item.unit || "none",
            rate: toNumber(item.rate),
            unitPrice: toNumber(item.rate),
            priceMode: item.priceMode,
            discountType: item.discountType,
            discountValue: toNumber(item.discountValue),
            grossAmount: roundMoney(item.grossAmount),
            discountAmount: roundMoney(item.discountAmount),
            taxableAmount: roundMoney(item.taxableAmount),
            taxPercentage: toNumber(item.taxPercentage),
            taxAmount: roundMoney(item.taxAmount),
            cgstAmount: roundMoney(item.cgstAmount),
            sgstAmount: roundMoney(item.sgstAmount),
            igstAmount: roundMoney(item.igstAmount),
            cessPercentage: toNumber(item.cessPercentage),
            cessAmount: roundMoney(item.cessAmount),
            totalAmount: roundMoney(item.totalAmount),
            lineTotal: roundMoney(item.totalAmount),
          };
        }),

      totals: {
        subtotal: totals.subtotal,
        grossAmount: totals.subtotal,
        discountAmount: totals.discountAmount,
        totalDiscount: totals.discountAmount,
        taxableAmount: totals.taxableAmount,
        cgstAmount: totals.cgstAmount,
        sgstAmount: totals.sgstAmount,
        igstAmount: totals.igstAmount,
        taxAmount: totals.taxAmount,
        totalTax: totals.taxAmount,
        cessAmount: totals.cessAmount,
        roundOffAmount: totals.roundOffAmount,
        roundOff: totals.roundOffAmount,
        grandTotal: totals.grandTotal,
        totalAmount: totals.grandTotal,
        invoiceDiscountType: "NONE",
        invoiceDiscountValue: 0,
      },
    };
  };

  /*
  |--------------------------------------------------------------------------
  | SAVE
  |--------------------------------------------------------------------------
  */

  const saveQuotation = async ({
    saveAsDraft,
    redirectAfterSave = true,
    createNewAfterSave = false,
  }) => {
    setError(
      ""
    );

    setSuccessMessage(
      ""
    );

    const validationError =
      validateForm();

    if (
      validationError
    ) {
      setError(
        validationError
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return null;
    }

    const payload =
      buildPayload(
        saveAsDraft
      );

    try {
      const result =
        isEditMode
          ? await dispatch(
              updateQuotation({
                documentId:
                  id,

                payload,
              })
            )
          : await dispatch(
              createQuotation(
                payload
              )
            );

      if (
        result?.meta
          ?.requestStatus !==
        "fulfilled"
      ) {
        const responseError =
          result?.payload?.message ||
          result?.payload ||
          result?.error?.message ||
          "Unable to save quotation.";

        setError(
          typeof responseError ===
            "string"
            ? responseError
            : "Unable to save quotation."
        );

        return null;
      }

      const createdDocument =
        result?.payload?.document ||
        result?.payload?.data?.document ||
        result?.payload?.data ||
        result?.payload ||
        null;

      setSuccessMessage(
        isEditMode
          ? "Quotation updated successfully."
          : "Quotation created successfully."
      );

      if (
        createNewAfterSave
      ) {
        window.setTimeout(
          () => {
            history.replace(
              "/billing/quotations/create"
            );

            window.location.reload();
          },
          500
        );

        return createdDocument;
      }

      if (
        redirectAfterSave
      ) {
        window.setTimeout(
          () => {
            history.push(
              "/billing/quotations"
            );
          },
          600
        );
      }

      return createdDocument;
    } catch (
      submitError
    ) {
      setError(
        submitError?.message ||
        "Unable to save quotation."
      );

      return null;
    }
  };

  const getSavedDocumentId = (document) =>
    document?._id ||
    document?.id ||
    document?.documentId ||
    document?.data?._id ||
    document?.data?.id ||
    "";

  const openPdfView = async (mode) => {
    setShowShareMenu(false);

    let documentId = id;

    if (!documentId) {
      const savedDocument = await saveQuotation({
        saveAsDraft: true,
        redirectAfterSave: false,
      });

      documentId = getSavedDocumentId(savedDocument);
    }

    if (!documentId) {
      setError(
        "Please save the quotation before opening the PDF view."
      );
      return;
    }

    history.push(
      `/billing/quotations/${documentId}/print?mode=${mode}`
    );
  };

  const handleSaveDraft = () => {
    saveQuotation({
      saveAsDraft:
        true,
    });
  };

  const handleSaveAndConfirm = () => {
    saveQuotation({
      saveAsDraft:
        false,
    });
  };

  const handleSaveAndNew = () => {
    setShowShareMenu(
      false
    );

    saveQuotation({
      saveAsDraft:
        true,

      redirectAfterSave:
        false,

      createNewAfterSave:
        true,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | SHARE / PRINT
  |--------------------------------------------------------------------------
  */

  const handleShare = () => {
    openPdfView("share");
  };

  const handlePrint = () => {
    openPdfView("print");
  };

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="quotation-form-page">
      <datalist id="quotation-customer-options">
        {customers.map(
          (
            customer,
            index
          ) => (
            <option
              key={
                getCustomerId(
                  customer
                ) ||
                index
              }
              value={getCustomerDisplayName(
                customer
              )}
            />
          )
        )}
      </datalist>

      <datalist id="quotation-product-options">
        {products.map(
          (
            product,
            index
          ) => (
            <option
              key={
                getProductId(
                  product
                ) ||
                index
              }
              value={getProductDisplayName(
                product
              )}
            />
          )
        )}
      </datalist>

      <header className="quotation-form-header">
        <div className="quotation-form-heading">
          <button
            type="button"
            aria-label="Back to quotations"
            onClick={() =>
              history.push(
                "/billing/quotations"
              )
            }
          >
            <FiArrowLeft />
          </button>

          <div className="quotation-form-heading-icon">
            <FiFileText />
          </div>

          <div>
            <h1>
              {isEditMode
                ? "Edit Quotation"
                : "Create Quotation"}
            </h1>

            <p>
              Select saved customers and products,
              or enter manual customer and item details.
            </p>
          </div>
        </div>
      </header>

      {error && (
        <div className="quotation-form-alert quotation-form-alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="quotation-form-alert quotation-form-alert-success">
          {successMessage}
        </div>
      )}

      <main className="quotation-erp-workspace">
      <section className="quotation-erp-section quotation-customer-section">
        <div className="quotation-section-title">
          <div>
            <h2>
              Customer & Quotation Details
            </h2>

            <p>
              Enter customer and quotation information.
            </p>
          </div>
        </div>

        <div className="quotation-customer-search-row">
          <div className="quotation-form-field quotation-customer-search-field">
            <label>
              Search or Enter Customer
              <span>*</span>
            </label>

            <div className="quotation-customer-input">
              <FiUser />

              <input
                type="text"
                list="quotation-customer-options"
                value={
                  formData.customerSearch
                }
                placeholder="Search name/mobile or type customer name"
                onChange={
                  handleCustomerSearchChange
                }
                autoComplete="off"
              />
            </div>

            <small className="quotation-form-help">
              Type any name when the customer is not saved.
            </small>
          </div>

          <div className="quotation-form-field">
            <label>
              Quotation Date
              <span>*</span>
            </label>

            <input
              type="date"
              name="documentDate"
              value={
                formData.documentDate
              }
              onChange={
                handleFieldChange
              }
            />
          </div>

          <div className="quotation-form-field">
            <label>
              Validity Days
            </label>

            <input
              type="number"
              name="validityDays"
              min="0"
              value={
                formData.validityDays
              }
              onChange={
                handleFieldChange
              }
            />
          </div>

          <div className="quotation-form-field">
            <label>
              Valid Until
              <span>*</span>
            </label>

            <input
              type="date"
              name="validUntil"
              min={
                formData.documentDate ||
                undefined
              }
              value={
                formData.validUntil
              }
              onChange={
                handleFieldChange
              }
            />
          </div>
        </div>

        <div className="quotation-form-grid quotation-customer-primary-grid">
          <div className="quotation-form-field">
            <label>
              Customer Name
              <span>*</span>
            </label>

            <input
              type="text"
              name="customerName"
              value={
                formData.customerName
              }
              onChange={
                handleCustomerFieldChange
              }
              placeholder="Customer name"
            />
          </div>

          <div className="quotation-form-field">
            <label>
              Mobile
            </label>

            <input
              type="text"
              name="customerMobile"
              value={
                formData.customerMobile
              }
              onChange={
                handleCustomerFieldChange
              }
              placeholder="Mobile number"
              maxLength={15}
            />
          </div>

          <div className="quotation-form-field">
            <label>
              Email
            </label>

            <input
              type="email"
              name="customerEmail"
              value={
                formData.customerEmail
              }
              onChange={
                handleCustomerFieldChange
              }
              placeholder="Email address"
            />
          </div>

          <div className="quotation-customer-more-actions quotation-customer-more-actions-left">
            <button
              type="button"
              className={[
                "quotation-customer-detail-button",

                showAddressDetails
                  ? "quotation-customer-detail-button-active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                setShowAddressDetails(
                  (current) =>
                    !current
                )
              }
            >
              <FiMapPin />

              <span>
                {showAddressDetails
                  ? "Hide Address"
                  : "Add Address"}
              </span>

              {showAddressDetails ? (
                <FiChevronUp />
              ) : (
                <FiChevronDown />
              )}
            </button>

            <button
              type="button"
              className={[
                "quotation-customer-detail-button",

                showGstDetails
                  ? "quotation-customer-detail-button-active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                setShowGstDetails(
                  (current) =>
                    !current
                )
              }
            >
              <FiTag />

              <span>
                {showGstDetails
                  ? "Hide GST"
                  : "Add GST Details"}
              </span>

              {showGstDetails ? (
                <FiChevronUp />
              ) : (
                <FiChevronDown />
              )}
            </button>
          </div>
        </div>

        {showAddressDetails && (
          <div className="quotation-optional-details-panel">
            <div className="quotation-optional-details-header">
              <div className="quotation-optional-details-heading">
                <FiMapPin />

                <div>
                  <strong>
                    Customer Address
                  </strong>

                  <span>
                    Optional billing and delivery details
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="quotation-optional-clear-button"
                onClick={
                  clearAddressDetails
                }
              >
                <FiX />

                Clear
              </button>
            </div>

            <div className="quotation-form-grid quotation-address-grid">
              <div className="quotation-form-field quotation-form-field-wide">
                <label>
                  Address
                </label>

                <input
                  type="text"
                  name="customerAddress"
                  value={
                    formData.customerAddress
                  }
                  onChange={
                    handleCustomerFieldChange
                  }
                  placeholder="Customer address"
                />
              </div>

              <div className="quotation-form-field">
                <label>
                  City
                </label>

                <input
                  type="text"
                  name="customerCity"
                  value={
                    formData.customerCity
                  }
                  onChange={
                    handleCustomerFieldChange
                  }
                  placeholder="City"
                />
              </div>

              <div className="quotation-form-field">
                <label>
                  Pincode
                </label>

                <input
                  type="text"
                  name="customerPincode"
                  value={
                    formData.customerPincode
                  }
                  onChange={
                    handleCustomerFieldChange
                  }
                  placeholder="Pincode"
                  maxLength={6}
                />
              </div>
            </div>
          </div>
        )}

        {showGstDetails && (
          <div className="quotation-optional-details-panel">
            <div className="quotation-optional-details-header">
              <div className="quotation-optional-details-heading">
                <FiTag />

                <div>
                  <strong>
                    GST Information
                  </strong>

                  <span>
                    Required for registered business customers
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="quotation-optional-clear-button"
                onClick={
                  clearGstDetails
                }
              >
                <FiX />

                Clear
              </button>
            </div>

            <div className="quotation-form-grid quotation-gst-grid">
              <div className="quotation-form-field">
                <label>
                  Customer GSTIN
                </label>

                <input
                  type="text"
                  name="customerGstin"
                  value={
                    formData.customerGstin
                  }
                  onChange={
                    handleCustomerFieldChange
                  }
                  placeholder="15 digit GSTIN"
                  maxLength={15}
                />
              </div>

              <div className="quotation-form-field">
                <label>
                  State of Supply
                </label>

                <select
                  value={
                    formData.placeOfSupplyStateCode
                  }
                  onChange={
                    handleStateChange
                  }
                >
                  <option value="">
                    Select State
                  </option>

                  {INDIA_STATES.map(
                    (state) => (
                      <option
                        key={
                          state.code
                        }
                        value={
                          state.code
                        }
                      >
                        {state.name}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="quotation-form-field">
                <label>
                  Default Price Mode
                </label>

                <select
                  name="priceMode"
                  value={formData.priceMode}
                  onChange={handleFieldChange}
                >
                  <option value="TAX_EXCLUSIVE">
                    Without Tax
                  </option>

                  <option value="TAX_INCLUSIVE">
                    With Tax
                  </option>
                </select>
              </div>

              <div className="quotation-form-field">
                <label>
                  Tax Type
                </label>

                <div
                  className={[
                    "quotation-tax-type-preview",

                    isInterState
                      ? "quotation-tax-type-igst"
                      : "quotation-tax-type-gst",
                  ].join(" ")}
                >
                  {isInterState
                    ? "IGST"
                    : "CGST + SGST"}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="quotation-form-grid quotation-document-options">
          <div className="quotation-form-field">
            <label>
              Sales Channel
            </label>

            <select
              name="salesChannel"
              value={
                formData.salesChannel
              }
              onChange={
                handleFieldChange
              }
            >
              {[
                "STORE",
                "ONLINE",
                "WHATSAPP",
                "PHONE",
                "POS",
                "OTHER",
              ].map(
                (channel) => (
                  <option
                    key={
                      channel
                    }
                    value={
                      channel
                    }
                  >
                    {channel.replace(
                      /_/g,
                      " "
                    )}
                  </option>
                )
              )}
            </select>
          </div>

          {branches.length > 0 && (
            <div className="quotation-form-field">
              <label>
                Branch
              </label>

              <select
                name="branchId"
                value={
                  formData.branchId
                }
                onChange={
                  handleFieldChange
                }
              >
                <option value="">
                  Select Branch
                </option>

                {branches.map(
                  (branch) => (
                    <option
                      key={
                        branch._id ||
                        branch.id
                      }
                      value={
                        branch._id ||
                        branch.id
                      }
                    >
                      {branch.branchName ||
                        branch.name}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {firms.length > 0 && (
            <div className="quotation-form-field">
              <label>
                Firm
              </label>

              <select
                name="firmId"
                value={
                  formData.firmId
                }
                onChange={
                  handleFieldChange
                }
              >
                <option value="">
                  Select Firm
                </option>

                {firms.map(
                  (firm) => (
                    <option
                      key={
                        firm._id ||
                        firm.id
                      }
                      value={
                        firm._id ||
                        firm.id
                      }
                    >
                      {firm.firmName ||
                        firm.name}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {warehouses.length > 0 && (
            <div className="quotation-form-field">
              <label>
                Warehouse
              </label>

              <select
                name="warehouseId"
                value={
                  formData.warehouseId
                }
                onChange={
                  handleFieldChange
                }
              >
                <option value="">
                  Select Warehouse
                </option>

                {warehouses.map(
                  (warehouse) => (
                    <option
                      key={
                        warehouse._id ||
                        warehouse.id
                      }
                      value={
                        warehouse._id ||
                        warehouse.id
                      }
                    >
                      {warehouse.warehouseName ||
                        warehouse.name}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {salespersons.length > 0 && (
            <div className="quotation-form-field">
              <label>
                Salesperson
              </label>

              <select
                name="salesperson"
                value={
                  formData.salesperson
                }
                onChange={
                  handleFieldChange
                }
              >
                <option value="">
                  Select Salesperson
                </option>

                {salespersons.map(
                  (salesperson) => (
                    <option
                      key={
                        salesperson._id ||
                        salesperson.id
                      }
                      value={
                        salesperson._id ||
                        salesperson.id
                      }
                    >
                      {salesperson.employeeName ||
                        salesperson.name}
                    </option>
                  )
                )}
              </select>
            </div>
          )}
        </div>
      </section>

      <section className="quotation-erp-section quotation-items-section">
        <div className="quotation-items-header">
          <div>
            <h2>
              Products / Items
            </h2>

            <p>
              Select an inventory product or type a manual item.
              Drag rows to change their order.
            </p>
          </div>

          <button
            type="button"
            onClick={
              addItem
            }
          >
            <FiPlus />

            Add Row
          </button>
        </div>

        <div className="quotation-items-scroll">
          <table className="quotation-items-table">
            <colgroup>
              <col className="col-drag" />
              <col className="col-item" />
              <col className="col-description" />
              <col className="col-qty" />
              <col className="col-unit" />
              <col className="col-price-mode" />
              <col className="col-tax" />
              <col className="col-price" />
              <col className="col-discount" />
              <col className="col-total" />
              <col className="col-action" />
            </colgroup>

            <thead>
              <tr>
                <th aria-label="Drag row" />

                <th>
                  Item
                </th>

                <th>
                  Description
                </th>

                <th>
                  Qty
                </th>

                <th>
                  Unit
                </th>

                <th>
                  Price Mode
                </th>

                <th>
                  Tax
                </th>

                <th>
                  Price/Unit
                </th>

                <th>
                  Discount
                </th>

                <th>
                  Amount
                </th>

                <th>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {calculatedItems.map(
                (item) => (
                  <tr
                    key={
                      item.temporaryId
                    }
                    onDragOver={(
                      event
                    ) =>
                      handleDragOver(
                        event,
                        item.temporaryId
                      )
                    }
                    onDragLeave={() =>
                      handleDragLeave(
                        item.temporaryId
                      )
                    }
                    onDrop={(
                      event
                    ) =>
                      handleDrop(
                        event,
                        item.temporaryId
                      )
                    }
                    className={
                      draggedOverRowId ===
                      item.temporaryId
                        ? "quotation-row-drag-over"
                        : ""
                    }
                  >
                    <td>
                      <button
                        type="button"
                        className="quotation-drag-handle"
                        title="Drag row"
                        draggable
                        tabIndex={-1}
                        onDragStart={(event) =>
                          handleDragStart(
                            event,
                            item.temporaryId
                          )
                        }
                        onDragEnd={handleDragEnd}
                      >
                        <FiMenu />
                      </button>
                    </td>

                    <td>
                      <input
                        type="text"
                        list="quotation-product-options"
                        value={
                          item.productSearch
                        }
                        onChange={(
                          event
                        ) =>
                          handleProductSearchChange(
                            item.temporaryId,
                            event.target.value
                          )
                        }
                        placeholder="Search or type item"
                        autoComplete="off"
                      />

                      <small className="quotation-item-type">
                        {item.productId
                          ? "Inventory"
                          : "Manual"}
                      </small>
                    </td>

                    <td>
                      <input
                        type="text"
                        value={
                          item.description
                        }
                        onChange={(
                          event
                        ) =>
                          updateItem(
                            item.temporaryId,
                            "description",
                            event.target.value
                          )
                        }
                        placeholder="Description"
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="quotation-qty-input"
                        min="0.01"
                        step="0.01"
                        value={
                          item.quantity
                        }
                        onChange={(
                          event
                        ) =>
                          updateItem(
                            item.temporaryId,
                            "quantity",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td>
                      <select
                        className="quotation-unit-select"
                        value={
                          item.unit
                        }
                        onChange={(
                          event
                        ) =>
                          updateItem(
                            item.temporaryId,
                            "unit",
                            event.target.value
                          )
                        }
                      >
                        {UNIT_OPTIONS.map(
                          (unit) => (
                            <option
                              key={
                                unit.value
                              }
                              value={
                                unit.value
                              }
                            >
                              {unit.label}
                            </option>
                          )
                        )}
                      </select>
                    </td>

                    <td>
                      <select
                        value={
                          item.priceMode
                        }
                        onChange={(
                          event
                        ) =>
                          updateItem(
                            item.temporaryId,
                            "priceMode",
                            event.target.value
                          )
                        }
                      >
                        <option value="TAX_EXCLUSIVE">
                          Without Tax
                        </option>

                        <option value="TAX_INCLUSIVE">
                          With Tax
                        </option>
                      </select>
                    </td>

                    <td>
                      <div className="quotation-paired-field">
                        <select
                          value={
                            item.taxPercentage
                          }
                          onChange={(
                            event
                          ) =>
                            updateItem(
                              item.temporaryId,
                              "taxPercentage",
                              event.target.value
                            )
                          }
                        >
                          {TAX_OPTIONS.map(
                            (rate) => (
                              <option
                                key={
                                  rate
                                }
                                value={
                                  rate
                                }
                              >
                                {rate === 0
                                  ? "NONE"
                                  : `${
                                      isInterState
                                        ? "IGST"
                                        : "GST"
                                    } @ ${rate}%`}
                              </option>
                            )
                          )}
                        </select>

                        <div className="quotation-paired-result">
                          <span>
                            Tax
                          </span>

                          <strong>
                            {formatCurrency(
                              item.taxAmount
                            )}
                          </strong>
                        </div>
                      </div>

                      {item.taxPercentage > 0 && (
                        <small className="quotation-item-tax-label">
                          {isInterState
                            ? `IGST ${item.taxPercentage}%`
                            : `CGST ${
                                item.taxPercentage /
                                2
                              }% + SGST ${
                                item.taxPercentage /
                                2
                              }%`}
                        </small>
                      )}
                    </td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          item.rate
                        }
                        onChange={(
                          event
                        ) =>
                          updateItem(
                            item.temporaryId,
                            "rate",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td>
                      <div className="quotation-discount-pair">
                        <select
                          value={
                            item.discountType
                          }
                          onChange={(
                            event
                          ) =>
                            updateItem(
                              item.temporaryId,
                              "discountType",
                              event.target.value
                            )
                          }
                        >
                          <option value="NONE">
                            None
                          </option>

                          <option value="PERCENTAGE">
                            %
                          </option>

                          <option value="FLAT">
                            Flat
                          </option>
                        </select>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={
                            item.discountValue
                          }
                          disabled={
                            item.discountType ===
                            "NONE"
                          }
                          onChange={(
                            event
                          ) =>
                            updateItem(
                              item.temporaryId,
                              "discountValue",
                              event.target.value
                            )
                          }
                          placeholder={
                            item.discountType ===
                            "PERCENTAGE"
                              ? "%"
                              : "₹"
                          }
                        />

                        <div className="quotation-discount-result">
                          <span>
                            Discount
                          </span>

                          <strong>
                            {formatCurrency(
                              item.discountAmount
                            )}
                          </strong>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="quotation-line-total">
                        <strong>
                          {formatCurrency(
                            item.totalAmount
                          )}
                        </strong>

                        {item.priceMode ===
                          "TAX_INCLUSIVE" && (
                          <span>
                            Tax included
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="quotation-item-delete"
                        onClick={() =>
                          removeItem(
                            item.temporaryId
                          )
                        }
                        title="Remove item"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={3}>
                  <button
                    type="button"
                    className="quotation-add-row-button"
                    onClick={
                      addItem
                    }
                  >
                    <FiPlus />

                    Add Row
                  </button>
                </td>

                <td>
                  <strong>
                    {totals.totalQuantity}
                  </strong>
                </td>

                <td colSpan={4}>
                  <strong>
                    Total Items:{" "}
                    {totals.itemCount}
                  </strong>
                </td>

                <td>
                  <strong>
                    Discount:{" "}
                    {formatCurrency(
                      totals.discountAmount
                    )}
                  </strong>
                </td>

                <td>
                  <strong>
                    {formatCurrency(
                      totals.totalBeforeRoundOff
                    )}
                  </strong>
                </td>

                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div className="quotation-form-bottom-grid">
        <section className="quotation-erp-section quotation-notes-section">
          <div className="quotation-section-title">
            <div>
              <h2>
                Notes & Terms
              </h2>

              <p>
                Add customer-facing and internal information.
              </p>
            </div>
          </div>

          <div className="quotation-notes-grid">
            <div className="quotation-form-field">
              <label>
                Customer Notes
              </label>

              <textarea
                name="customerNotes"
                rows="3"
                value={
                  formData.customerNotes
                }
                onChange={
                  handleFieldChange
                }
                placeholder="Notes visible to customer"
              />
            </div>

            <div className="quotation-form-field">
              <label>
                Terms and Conditions
              </label>

              <textarea
                name="termsAndConditions"
                rows="3"
                value={
                  formData.termsAndConditions
                }
                onChange={
                  handleFieldChange
                }
                placeholder="Terms and conditions"
              />
            </div>

            <div className="quotation-form-field quotation-internal-notes-field">
              <label>
                Internal Notes
              </label>

              <textarea
                name="internalNotes"
                rows="2"
                value={
                  formData.internalNotes
                }
                onChange={
                  handleFieldChange
                }
                placeholder="Internal notes, not visible to customer"
              />
            </div>
          </div>
        </section>

        <section className="quotation-erp-section quotation-total-card">
          <div className="quotation-summary-title">
            <div>
              <h2>
                Quotation Summary
              </h2>

              <span>
                {totals.itemCount} item
                {totals.itemCount !== 1
                  ? "s"
                  : ""}
              </span>
            </div>
          </div>

          <div className="quotation-total-row">
            <span>
              Subtotal
            </span>

            <strong>
              {formatCurrency(
                totals.subtotal
              )}
            </strong>
          </div>

          <div className="quotation-total-row">
            <span>
              Discount
            </span>

            <strong
              className={
                totals.discountAmount > 0
                  ? "quotation-summary-negative"
                  : ""
              }
            >
              {totals.discountAmount > 0
                ? "-"
                : ""}
              {formatCurrency(
                totals.discountAmount
              )}
            </strong>
          </div>

          <div className="quotation-total-row">
            <span>
              Taxable Amount
            </span>

            <strong>
              {formatCurrency(
                totals.taxableAmount
              )}
            </strong>
          </div>

          {!isInterState && (
            <>
              <div className="quotation-total-row">
                <span>
                  CGST
                </span>

                <strong>
                  {formatCurrency(
                    totals.cgstAmount
                  )}
                </strong>
              </div>

              <div className="quotation-total-row">
                <span>
                  SGST
                </span>

                <strong>
                  {formatCurrency(
                    totals.sgstAmount
                  )}
                </strong>
              </div>
            </>
          )}

          {isInterState && (
            <div className="quotation-total-row">
              <span>
                IGST
              </span>

              <strong>
                {formatCurrency(
                  totals.igstAmount
                )}
              </strong>
            </div>
          )}

          <div className="quotation-total-row">
            <span>
              Total Tax
            </span>

            <strong>
              {formatCurrency(
                totals.taxAmount
              )}
            </strong>
          </div>

          <div className="quotation-total-row quotation-round-off-row">
            <label>
              <input
                type="checkbox"
                name="enableRoundOff"
                checked={
                  formData.enableRoundOff
                }
                onChange={
                  handleFieldChange
                }
              />

              Round Off
            </label>

            <strong>
              {formatCurrency(
                totals.roundOffAmount
              )}
            </strong>
          </div>

          <div className="quotation-total-row quotation-total-grand">
            <span>
              Grand Total
            </span>

            <strong>
              {formatCurrency(
                totals.grandTotal
              )}
            </strong>
          </div>
        </section>
      </div>

      </main>

      <footer className="quotation-form-actions">
        <div className="quotation-form-actions-left">
          <span>
            {totals.itemCount} item
            {totals.itemCount !== 1
              ? "s"
              : ""}
          </span>

          <strong>
            {formatCurrency(
              totals.grandTotal
            )}
          </strong>
        </div>

        <div className="quotation-form-actions-right">
          <button
            type="button"
            className="quotation-form-cancel"
            onClick={() =>
              history.push(
                "/billing/quotations"
              )
            }
            disabled={
              saving
            }
          >
            Cancel
          </button>

          <button
            type="button"
            className="quotation-form-draft"
            onClick={
              handleSaveDraft
            }
            disabled={
              saving
            }
          >
            <FiSave />

            {saving
              ? "Saving..."
              : "Save Draft"}
          </button>

          <div
            ref={
              shareMenuRef
            }
            className="quotation-share-wrapper"
          >
            <div className="quotation-share-split">
              <button
                type="button"
                className="quotation-share-main"
                onClick={
                  handleShare
                }
                disabled={
                  saving
                }
              >
                <FiShare2 />

                Share
              </button>

              <button
                type="button"
                className="quotation-share-toggle"
                onClick={() =>
                  setShowShareMenu(
                    (current) =>
                      !current
                  )
                }
                disabled={
                  saving
                }
                aria-label="Open share actions"
              >
                <FiChevronDown />
              </button>
            </div>

            {showShareMenu && (
              <div className="quotation-share-menu">
                <button
                  type="button"
                  onClick={
                    handleShare
                  }
                >
                  <FiShare2 />

                  Share
                </button>

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                >
                  <FiPrinter />

                  Print
                </button>

                <button
                  type="button"
                  onClick={
                    handleSaveAndNew
                  }
                >
                  <FiPlus />

                  Save & New
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="quotation-form-save"
            onClick={
              handleSaveAndConfirm
            }
            disabled={
              saving
            }
          >
            <FiSave />

            {saving
              ? "Saving..."
              : isEditMode
                ? "Update & Confirm"
                : "Save & Confirm"}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default QuotationFormPage;